import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { Shield, Mail, Lock, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import creditReportMoneyLogo from '@/assets/credit-report-money-logo.png';

const authSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  
  const { signIn, signUp, user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validation = authSchema.safeParse({ email, password });
    if (!validation.success) {
      const fieldErrors: { email?: string; password?: string } = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0] === 'email') fieldErrors.email = err.message;
        if (err.path[0] === 'password') fieldErrors.password = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast({
              title: 'Login Failed',
              description: 'Invalid email or password. Please try again.',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Login Failed',
              description: error.message,
              variant: 'destructive',
            });
          }
        } else {
          toast({
            title: 'Welcome back!',
            description: 'You have been logged in successfully.',
          });
        }
      } else {
        const { error } = await signUp(email, password);
        if (error) {
          if (error.message.includes('User already registered')) {
            toast({
              title: 'Account Exists',
              description: 'An account with this email already exists. Try logging in instead.',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Signup Failed',
              description: error.message,
              variant: 'destructive',
            });
          }
        } else {
          toast({
            title: 'Account created!',
            description: 'Welcome! Setting up your organization...',
          });
          navigate('/onboarding');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[50%] h-[50%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[10%] -right-[10%] w-[40%] h-[40%]" />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex relative overflow-hidden">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[50%] h-[50%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[20%] -right-[10%] w-[40%] h-[40%]" />
        <div className="absolute inset-0 radar-grid opacity-20" />
      </div>

      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />
        
        <div className="relative z-10 flex flex-col justify-center px-16 animate-reveal">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <img 
              src={creditReportMoneyLogo} 
              alt="Credit Report Money" 
              className="h-16 w-auto"
            />
          </Link>
          
          <h1 className="text-4xl font-bold mb-4">
            <span className="gradient-text-gold">AI-Powered</span>
            <br />
            <span className="text-foreground">Credit Report Analysis</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
            Identify FCRA violations automatically, generate dispute letters, and help your clients improve their credit scores.
          </p>
          
          <div className="mt-12 space-y-4">
            {[
              { step: '1', text: 'Upload credit reports', color: 'primary' },
              { step: '2', text: 'AI analyzes for violations', color: 'primary' },
              { step: '3', text: 'Generate dispute letters', color: 'accent' },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-3 text-muted-foreground animate-reveal`}
                style={{ animationDelay: `${(idx + 1) * 100}ms` }}
              >
                <div className={`w-10 h-10 rounded-xl bg-${item.color}/10 border border-${item.color}/20 flex items-center justify-center`}>
                  <span className={`text-${item.color} text-sm font-bold`}>{item.step}</span>
                </div>
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-reveal">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Link to="/">
              <img 
                src={creditReportMoneyLogo} 
                alt="Credit Report Money" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          <div className="glass-panel-strong rounded-2xl p-8 border border-border/50">
            <div className="flex items-center gap-2 text-accent mb-6 justify-center">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">10 free scans included!</span>
            </div>

            <h2 className="text-2xl font-bold text-foreground mb-2 text-center">
              {isLogin ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="text-muted-foreground mb-8 text-center">
              {isLogin 
                ? 'Enter your credentials to access your dashboard' 
                : 'Start analyzing credit reports in minutes'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-card/50 border-border/50 focus:border-primary/50"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-card/50 border-border/50 focus:border-primary/50"
                    disabled={isLoading}
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full btn-glow bg-gradient-to-r from-primary to-primary/90" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                {isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setErrors({});
                }}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {isLogin 
                  ? "Don't have an account? Sign up" 
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
